import { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { FilterContext, FilterDispatchContext } from "../../Context/FilterContext";

const Wrapper = styled.div`
  position: relative;

  .location-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    border: none;
    border-radius: 8px;
    background: #fff;
    font-size: 14px;
    color: var(--primary-grey-500);
    cursor: pointer;
    box-shadow: var(--elevation-1dp);
    transition: all 150ms ease;
    white-space: nowrap;

    &:hover, &.open {
      box-shadow: var(--elevation-2dp);
    }

    &.active {
      background: var(--primary-100);
    }
  }

  .location-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 100;
    width: 280px;
    max-height: 320px;
    background: #fff;
    border-radius: 8px;
    box-shadow: var(--elevation-2dp);
    display: flex;
    flex-direction: column;

    input {
      padding: 12px 14px;
      border: none;
      border-bottom: 1px solid var(--background-grey-200);
      border-radius: 8px 8px 0 0;
      font-size: 14px;
      outline: none;

      &::placeholder {
        color: var(--color-secondary-200);
      }
    }

    .location-list {
      overflow-y: auto;
      flex: 1;
    }

    .location-option {
      padding: 10px 14px;
      font-size: 14px;
      cursor: pointer;
      color: var(--primary-grey-500);
      transition: background 100ms;

      &:hover, &.highlighted {
        background: var(--background-grey-100);
      }

      &.selected {
        background: var(--primary-100);
        font-weight: 600;
      }
    }
  }
`;

const LocationCombobox = () => {
  const filters = useContext(FilterContext) || {};
  const dispatch = useContext(FilterDispatchContext);
  const currentLocation = filters.location || "";

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cities, setCities] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_WEB_URL || process.env.REACT_APP_API_URL;
    axios.get(`${baseUrl}/cities.json`).then((res) => {
      const sorted = (res.data.results || []).sort();
      setCities(sorted);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = query
    ? cities.filter((c) => c.toLowerCase().includes(query.toLowerCase()))
    : cities;

  const options = ["", ...filtered]; // "" = All locations

  const handleSelect = (city) => {
    if (city === "") {
      dispatch({ action: "delete", filter: { key: "location" } });
    } else {
      dispatch({ action: "set", filter: { key: "location", value: city } });
    }
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(options[highlightIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  useEffect(() => {
    if (listRef.current) {
      const highlighted = listRef.current.children[highlightIndex];
      if (highlighted) highlighted.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  const label = currentLocation || "Location";
  const isActive = !!currentLocation;

  return (
    <Wrapper ref={wrapperRef}>
      <button
        type="button"
        className={`location-trigger${open ? " open" : ""}${isActive ? " active" : ""}`}
        onClick={() => setOpen(!open)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
        {label}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
          <path d="M2 4l3 3 3-3"/>
        </svg>
      </button>

      {open && (
        <div className="location-dropdown">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search city..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="location-list" ref={listRef}>
            {options.map((city, idx) => (
              <div
                key={city || "__all__"}
                className={`location-option${idx === highlightIndex ? " highlighted" : ""}${city === currentLocation || (city === "" && !currentLocation) ? " selected" : ""}`}
                onClick={() => handleSelect(city)}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                {city || "All locations"}
              </div>
            ))}
            {options.length === 0 && (
              <div className="location-option">No results</div>
            )}
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default LocationCombobox;
